import axios, { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from 'react-query';

import type { UploadUrlResponse } from '~/types/api';
import { GreetDetails } from '~/types/models';
import { DefaultGetResponse } from './rest';

export const queryKeys = {
  all: ['greets'] as const,
  details: () => [...queryKeys.all, 'details'] as const,
  detail: (id: string) => [...queryKeys.details(), id] as const,
};

export const uploadUrl = (greetId: string) =>
  axios.get<UploadUrlResponse>(`/api/v2/greets/${greetId}/upload-url`);

export const saveGreet = (
  greetId: string,
  body: FormData | Record<string, string>
) => axios.put(`/api/v3/greets/${greetId}`, body);

export async function fetchGreet(id: string) {
  const res = await axios.get<DefaultGetResponse<GreetDetails>>(
    `/api/v2/greets/${id}`
  );

  return res.data;
}

export interface UseGreetParams {
  id: string;
  options?: UseQueryOptions<GreetDetails, AxiosError>;
}

export function useGreet({ id, options }: UseGreetParams) {
  return useQuery<GreetDetails, AxiosError>(
    queryKeys.detail(id),
    () => fetchGreet(id),
    options
  );
}

export interface PostUploadStartedResponse {
  message: string;
}

export async function postUploadStarted(id: string) {
  const res = await axios.post<PostUploadStartedResponse>(
    `/api/v2/greets/${id}/upload-started`
  );

  return res.data;
}

export type UseUploadStartedMutationParams = UseMutationOptions<
  PostUploadStartedResponse,
  AxiosError,
  string
>;

export function useUploadStartedMutation(
  options?: UseUploadStartedMutationParams
) {
  return useMutation<PostUploadStartedResponse, AxiosError, string>(
    postUploadStarted,
    options
  );
}

export interface PostUploadFailedResponse {
  message: string;
}

export async function postUploadFailed(id: string) {
  const res = await axios.post<PostUploadFailedResponse>(
    `/api/v2/greets/${id}/upload-failed`
  );

  return res.data;
}

export type UseUploadFailedMutationParams = UseMutationOptions<
  PostUploadFailedResponse,
  AxiosError,
  string
>;

export function useUploadFailedMutation(
  options?: UseUploadFailedMutationParams
) {
  return useMutation<PostUploadFailedResponse, AxiosError, string>(
    postUploadFailed,
    options
  );
}

export interface DisableGreetFallbackResponse {
  message: string;
}

export async function disableGreetFallback(uuids: string[]) {
  const res = await axios.post<DisableGreetFallbackResponse>(
    '/api/v3/greets/disable-fallback',
    { uuids }
  );

  return res.data;
}

export interface UseDisableGreetFallbackParams {
  options?: UseMutationOptions<
    DisableGreetFallbackResponse,
    AxiosError,
    string[]
  >;
}

export function useDisableGreetFallback({
  options,
}: UseDisableGreetFallbackParams) {
  return useMutation<DisableGreetFallbackResponse, AxiosError, string[]>(
    disableGreetFallback,
    options
  );
}
